import { skipToken } from '@tanstack/react-query';

import { reportException } from '_lib/utils/sentryService';

import {
  createApiResource,
  CreateResource,
  CustomQueryHookResult,
  ListResource,
  PutResource,
  SkippableApiDetailOptions,
  UnskippableApiDetailOptions,
  useCustomQuery
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  PointmanCustomerCreateDTO,
  PointmanCustomerDetailDTO,
  PointmanCustomerListDTO,
  PointmanCustomerParams,
  PointmanCustomerUpdateDTO
} from '../apiTypes.shim';

type UnskippableApiDetailOptionsWithParams = UnskippableApiDetailOptions & { params: { companyId: string } };

type ApiDetailOptionsWithParams = SkippableApiDetailOptions | UnskippableApiDetailOptionsWithParams;

interface PointmanCustomerResource
  extends ListResource<PointmanCustomerListDTO, PointmanCustomerParams>,
    CreateResource<PointmanCustomerCreateDTO, PointmanCustomerDetailDTO>,
    PutResource<PointmanCustomerUpdateDTO, PointmanCustomerDetailDTO> {
  useGetById: (options: ApiDetailOptionsWithParams) => CustomQueryHookResult<PointmanCustomerDetailDTO>;
  getById: (options: UnskippableApiDetailOptionsWithParams) => Promise<PointmanCustomerDetailDTO>;
}

export const createPointmanCustomerResource = (): PointmanCustomerResource => {
  const pointmanCustomerResourceUri = `customer`;

  const { list, useList, listQueryKey, create, useCreate, put, usePut } = createApiResource(
    API,
    pointmanCustomerResourceUri
  );

  const getById = async (options: UnskippableApiDetailOptionsWithParams) => {
    try {
      const res = await API.get<PointmanCustomerDetailDTO>(`${pointmanCustomerResourceUri}/${options.id}`, {
        ...options,
        extraOptions: { retry: 1, ...options?.extraOptions }
      });
      return res.data;
    } catch (err) {
      if (!options?.disableErrorReporting) reportException(err);
      throw err;
    }
  };

  const useGetById = (options: ApiDetailOptionsWithParams) =>
    useCustomQuery({
      queryKey: [pointmanCustomerResourceUri, options.id, options.id ? options.params.companyId : null],
      queryFn: options.id ? async () => getById(options) : skipToken
    });

  return {
    getById,
    useGetById,
    list,
    useList,
    listQueryKey,
    create,
    useCreate,
    put,
    usePut
  };
};
